/**
 * frontend/pages/CreatorProfile.jsx
 * SoundDAO Sprint 4 M2 — Enhanced public creator profile page
 *
 * Route: /creator/:creator_id   (no auth required)
 *
 * Sections:
 *   1. Nav bar
 *   2. Hero media — full-width video player (Task 2.4) or avatar fallback
 *   3. Identity — artist name, genre pill, small avatar thumb (when video shown)
 *   4. Stats chips — Fans · Street Team · FReN (Task 2.5)
 *   5. Bio
 *   6. Social links with platform colors (Task 2.5)
 *   7. Recruit CTA — for logged-in Players (Task 2.5)
 *   8. FReN drop section
 *   9. Own profile actions
 *
 * Data: GET /api/creators/:creator_id
 *       POST /api/fren/waitlist
 *       GET  /api/fren/waitlist/:creator_id/me
 *       POST /api/creators/:creator_id/recruit  (auth — Prompt 1.5)
 *
 * Props:
 *   session  — Supabase auth session (may be null)
 *   user     — users row ({ id, persona, ... }) from useAuth — may be null
 */

import { isHeatPublic } from '../utils/heatDisplay';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { BRAND_ICONS } from '../icons/brandIcons';
import StageArc from '../components/StageArc';
import { authFetch } from '../lib/authFetch';
import { useRates, sound, plus } from '../lib/rates';
import { saveRecruitIntent } from '../lib/userStorage';
import './CreatorProfile.css';

// ── Playlist embed URL resolver ───────────────────────────────────────────────
// Returns an iframe-embeddable URL for the given playlist, or null if the
// platform doesn't support embedding (e.g. Apple Music).
function getEmbedUrl(url, platform) {
  try {
    if (platform === 'spotify') {
      const match = url.match(/playlist\/([a-zA-Z0-9]+)/);
      if (match) {
        return `https://open.spotify.com/embed/playlist/${match[1]}?utm_source=generator`;
      }
    }
    if (platform === 'soundcloud') {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false&color=%23f97316`;
    }
    if (platform === 'youtube') {
      const match = url.match(/list=([a-zA-Z0-9_-]+)/);
      if (match) {
        return `https://www.youtube.com/embed/videoseries?list=${match[1]}`;
      }
    }
    // Apple Music and others don't support iframe embeds
    return null;
  } catch {
    return null;
  }
}

// ── SVG brand icon renderer ────────────────────────────────────────────────────
function BrandIcon({ platform, size = 15 }) {
  const icon = BRAND_ICONS[platform];
  if (!icon) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={icon.color}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path d={icon.path} />
    </svg>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── Time formatter for posts ───────────────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// Post type icons
const POST_TYPE_ICON = { text: '✏️', photo: '📷', link: '🔗', video: '🎬' };

// Platform-colored social metadata
const SOCIAL_META = {
  spotify:     { label: 'Spotify',     color: '#1DB954' },
  apple_music: { label: 'Apple Music', color: '#FC3C44' },
  instagram:   { label: 'Instagram',   color: '#E1306C' },
  soundcloud:  { label: 'SoundCloud',  color: '#FF5500' },
  bandcamp:    { label: 'Bandcamp',    color: '#1DA0C3' },
};

const FREN_STATUS_LABEL = {
  'pre-launch':  'Coming Soon',
  'not-started': 'Coming Soon',
  not_started:   'Coming Soon',
  in_progress:   'In Progress',
  launched:      '🚀 Launched',
};

// ── Sprint 5A — Heat tier display ─────────────────────────────────────────────
function getHeatDisplay(tier) {
  const map = {
    'Cold':     '❄️ Cold',
    'Rising':   '🌡️ Rising',
    'Warm':     '🔥 Warm',
    'Hot':      '🔥🔥 Hot',
    'BREAKING': '⚡ BREAKING',
  };
  return map[tier] || '❄️ Cold';
}

// ── Sprint 5B M7 — Stage badge display ────────────────────────────────────────
function getStageDisplay(stage) {
  const map = {
    'Flair':    '🎭 Flair',
    'Burning':  '🔥 Burning',
    'Wildfire': '🌪️ Wildfire',
    'Phoenix':  '🔥 Phoenix',
  };
  return map[stage] || stage;
}

// ── Sprint 5C — Dynamic recruit CTA copy ──────────────────────────────────────
function getRecruitCopy(streetTeamSize, artistName) {
  const name = artistName || 'this artist';
  if (streetTeamSize === 0) return {
    eyebrow: 'Be first',
    headline: `Be ${name}'s first fan`,
    proof: '0 fans on the street team · be first',
  };
  if (streetTeamSize <= 10) return {
    eyebrow: 'Early access',
    headline: `Be one of ${name}'s first fans`,
    proof: `${streetTeamSize} of 100 street team spots claimed`,
  };
  if (streetTeamSize <= 50) return {
    eyebrow: 'Join the team',
    headline: `Join ${name}'s street team`,
    proof: `${streetTeamSize} of 100 spots claimed · ${100 - streetTeamSize} left`,
  };
  if (streetTeamSize < 100) return {
    eyebrow: 'Almost full',
    headline: `Claim your spot on ${name}'s team`,
    proof: `Only ${100 - streetTeamSize} spots left`,
  };
  return {
    eyebrow: 'Street team full',
    headline: `${name}'s street team is full`,
    proof: 'Join the waitlist for the next opening',
  };
}

// ── Drop Countdown (Sprint 5B M5) ─────────────────────────────────────────────
function DropCountdown({ scheduledAt, title, contentType, contentBody }) {
  const [timeLeft, setTimeLeft] = React.useState('');

  React.useEffect(() => {
    function update() {
      const diff = new Date(scheduledAt) - new Date();
      if (diff <= 0) { setTimeLeft(''); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(
        d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${s}s`
      );
    }
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [scheduledAt]);

  if (!timeLeft) return null;
  return (
    <div style={{
      background: 'rgba(249,115,22,0.15)',
      border: '1px solid rgba(249,115,22,0.4)',
      borderRadius: '10px',
      padding: '10px 14px',
      margin: '12px 0',
      textAlign: 'center',
    }}>
      <div style={{fontSize:'13px', opacity:0.8}}>
        🔥 {title || 'Something is dropping'}
      </div>
      <div style={{fontSize:'20px', fontWeight:'bold', marginTop:'4px'}}>
        {timeLeft}
      </div>
      {contentType && contentBody && (
        <div style={{
          marginTop: '8px',
          fontSize: '12px',
          opacity: 0.7,
          borderTop: '0.5px solid rgba(249,115,22,0.3)',
          paddingTop: '8px',
        }}>
          {contentType === 'link' ? (
            <span>🎵 Music dropping with this</span>
          ) : contentType === 'text' ? (
            <span>&ldquo;{contentBody.slice(0, 60)}{contentBody.length > 60 ? '…' : ''}&rdquo;</span>
          ) : (
            <span>Content dropping with this</span>
          )}
        </div>
      )}
    </div>
  );
}

// ── CreatorProfile ─────────────────────────────────────────────────────────────
export default function CreatorProfile({ session, user }) {
  const rates = useRates();
  const { creator_id } = useParams();
  const navigate = useNavigate();
  const user_id  = session?.user?.id;
  const videoRef = useRef(null);

  const [creator,     setCreator]     = useState(null);
  // Served STAGE_GATES from GET /api/creators/:id (punch-list item 2).
  const [stageGates,  setStageGates]  = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');

  // Video player state
  const [isMuted,     setIsMuted]     = useState(true);
  const [videoFailed, setVideoFailed] = useState(false);

  // Posts feed state
  const [posts,        setPosts]        = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);

  // FReN waitlist count (displayed on profile even though joining is Coming Soon)
  const [waitlistCount, setWaitlistCount] = useState(0);

  // Fan playlists submitted for this creator
  const [fanPlaylists,        setFanPlaylists]        = useState([]);
  const [fanPlaylistsLoading, setFanPlaylistsLoading] = useState(true);

  // Recruit CTA state
  const [recruitDone,    setRecruitDone]    = useState(false);
  // { pending, amount, already } from POST /recruit — 202 pending until the creator qualifies.
  const [recruitResult,  setRecruitResult]  = useState(null);
  const [recruitLoading, setRecruitLoading] = useState(false);
  const [recruitError,   setRecruitError]   = useState('');

  // Sprint 5C — street team SOUND earned
  const [streetTeamSoundEarned, setStreetTeamSoundEarned] = useState(0);

  // ── Load creator profile ────────────────────────────────────────────────────
  useEffect(() => {
    if (!creator_id) return;
    setLoading(true);
    authFetch(`${API_BASE}/api/creators/${creator_id}`)
      .then(r => r.ok ? r.json() : r.json().then(e => { throw new Error(e.error || 'Not found'); }))
      .then(({ creator: c, stage_gates }) => {
        setCreator(c);
        setStageGates(stage_gates || null);
        setLoading(false);
        try { posthog.capture('profile_visited', { creator_id, artist_name: c.artist_name }); } catch (_) { /* analytics non-fatal */ }
      })
      .catch(err => {
        // Punch-list item 3 backstop: a creator viewing their OWN profile before
        // completing onboarding gets a 404. Send them to finish it rather than
        // showing a not-found page about themselves. Applies wherever the link
        // came from, not just the nav.
        if (user_id && String(user_id) === String(creator_id)) {
          navigate('/creator-onboarding', { replace: true });
          return;
        }
        setError(err.message || 'Could not load creator profile.');
        setLoading(false);
      });
    // user_id and navigate are read by the self-404 redirect above.
  }, [creator_id, user_id, navigate]);

  // ── Load posts feed ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!creator_id) return;
    setPostsLoading(true);
    authFetch(`${API_BASE}/api/posts/${creator_id}?limit=5`)
      .then(r => r.ok ? r.json() : { posts: [] })
      .then(({ posts: p }) => setPosts(p || []))
      .catch(() => setPosts([]))
      .finally(() => setPostsLoading(false));
  }, [creator_id]);

  // ── Load waitlist count ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!creator_id) return;
    authFetch(`${API_BASE}/api/fren/waitlist/${creator_id}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setWaitlistCount(d.count || 0); })
      .catch(() => {});
  }, [creator_id]);

  // ── Load fan playlists submitted for this creator ────────────────────────
  useEffect(() => {
    if (!creator_id) return;
    setFanPlaylistsLoading(true);
    authFetch(`${API_BASE}/api/playlists/creator/${creator_id}?limit=10`)
      .then(r => r.ok ? r.json() : { playlists: [] })
      .then(({ playlists: p }) => setFanPlaylists(p || []))
      .catch(() => setFanPlaylists([]))
      .finally(() => setFanPlaylistsLoading(false));
  }, [creator_id]);

  // ── Sprint 5C — Fetch street team SOUND earned ─────────────────────────────
  useEffect(() => {
    if (!creator_id) return;
    authFetch(`${API_BASE}/api/creators/${creator_id}/street-team`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setStreetTeamSoundEarned(data.totalSoundEarned || 0); })
      .catch(() => {});
  }, [creator_id]);

  // ── Recruit this creator ────────────────────────────────────────────────────
  const handleRecruit = useCallback(async () => {
    if (!user_id) {
      if (creator?.user_id) {
        saveRecruitIntent(creator.user_id);
      }
      navigate('/');
      return;
    }
    if (recruitDone || recruitLoading) return;
    setRecruitLoading(true);
    setRecruitError('');
    try {
      // Prompt 1.5: the server verifies the recruit and builds the one
      // canonical key; the recruiter is the token holder.
      const res = await authFetch(`${API_BASE}/api/creators/${encodeURIComponent(creator.user_id)}/recruit`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ source: 'creator_profile' }),
      });
      const data = await res.json();
      if (res.ok || data.already_awarded) {
        setRecruitDone(true);
        setRecruitResult({ pending: !!data.pending, amount: Number(data.amount) || 0, already: !!data.already_awarded });
        try { posthog.capture('creator_recruited', { creator_id: creator.user_id, already: !!data.already_awarded }); } catch (_) { /* analytics non-fatal */ }
      } else {
        setRecruitError(data.error === 'recruit_via_claim_only'
          ? 'This artist joined through an Archive claim — the fans who dug them up were paid.'
          : (data.error || 'Could not recruit. Please try again.'));
      }
    } catch {
      setRecruitError('Network error. Please try again.');
    } finally {
      setRecruitLoading(false);
    }
  }, [user_id, creator, recruitDone, recruitLoading, navigate]);

  // ── Video mute toggle ───────────────────────────────────────────────────────
  const handleToggleMute = useCallback(() => {
    if (!videoRef.current) return;
    const newMuted = !isMuted;
    videoRef.current.muted = newMuted;
    setIsMuted(newMuted);
  }, [isMuted]);

  // ── Render states ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="cp-screen cp-screen--loading">
        <div className="cp-spinner" />
      </div>
    );
  }

  if (error || !creator) {
    return (
      <div className="cp-screen cp-screen--error">
        <div className="cp-error-emoji">🎵</div>
        <h2>Creator not found</h2>
        <p>{error || "This profile doesn't exist yet."}</p>
        <Link to="/creators" className="cp-back-link">Browse creators →</Link>
      </div>
    );
  }

  const socials      = creator.social_links || {};
  const hasSocials   = Object.values(socials).some(Boolean);
  const isOwnProfile = user_id === creator.user_id;
  const isPlayer     = user?.persona === 'player';
  const frenLive     = creator.fren_status === 'launched';
  const showVideo    = !!creator.video_url && !videoFailed;

  return (
    <div className="cp-screen">
      {/* Nav bar */}
      <nav className="cp-nav">
        <Link to="/creators" className="cp-nav-back">← Discover Artists</Link>
        {session && (
          <Link
            to={`/dashboard/${session?.user?.user_metadata?.persona || 'player'}`}
            className="cp-nav-dashboard"
          >
            Dashboard
          </Link>
        )}
      </nav>

      {/* ── Hero media — full-width video or avatar ── */}
      <div className="cp-hero-media">
        {showVideo ? (
          <div className="cp-video-wrap">
            <video
              ref={videoRef}
              src={creator.video_url}
              className="cp-video"
              /* Sprint 6-Zero M6 item 6: the avatar stands in until the first
                 frame decodes, so the hero is the artist rather than a black
                 rectangle. Without this the desktop fold was solid black while
                 the video buffered. */
              poster={creator.avatar_url || undefined}
              preload="metadata"
              autoPlay
              muted
              loop
              playsInline
              onError={() => setVideoFailed(true)}
            />
            <button
              className="cp-video-mute-btn"
              onClick={handleToggleMute}
              aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            >
              {isMuted ? '🔇' : '🔊'}
            </button>
          </div>
        ) : (
          <div className="cp-hero-avatar-wrap">
            {creator.avatar_url ? (
              <img src={creator.avatar_url} alt={creator.artist_name} className="cp-avatar" />
            ) : (
              <div className="cp-avatar-fallback">
                {(creator.artist_name || '?')[0].toUpperCase()}
              </div>
            )}
            {creator.fren_status === 'launched' && (
              <div className="cp-avatar-badge">🚀</div>
            )}
          </div>
        )}
      </div>

      <div className="cp-content">
        {/* ── Identity ── */}
        <div className="cp-identity">
          {/* Small avatar thumb shown alongside name when video is the hero */}
          {showVideo && creator.avatar_url && (
            <img
              src={creator.avatar_url}
              alt={creator.artist_name}
              className="cp-identity-thumb"
            />
          )}
          <div className="cp-identity-text">
            <h1 className="cp-artist-name">{creator.artist_name}</h1>
            {creator.stage && creator.stage !== 'Raw' && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(249,115,22,0.15)',
                border: '1px solid rgba(249,115,22,0.35)',
                borderRadius: '20px',
                padding: '4px 12px',
                fontSize: '13px',
                fontWeight: '600',
                marginTop: '6px',
              }}>
                {getStageDisplay(creator.stage)}
              </div>
            )}
            {creator.genre && (
              <span className="cp-genre-chip">{creator.genre}</span>
            )}
          </div>
        </div>

        {/* ── Drop Countdown (Sprint 5C — elevated above stats) ── */}
        {creator.drop_scheduled_at &&
         new Date(creator.drop_scheduled_at) > new Date() && (
          <DropCountdown
            scheduledAt={creator.drop_scheduled_at}
            title={creator.drop_title}
            contentType={creator.drop_content_type}
            contentBody={creator.drop_content_body}
          />
        )}

        {/* ── Mini Stage Arc (Sprint 5C) ── */}
        <div className="cp-arc-strip">
          {/* Punch-list item 2: this arc showed "3 fans to Flair" for a creator
              who already met the 2-fan Season Zero gate, because StageArc owned
              its own pre-Season-Zero constants. Gates are served now. */}
          <StageArc
            mini
            stage={creator.stage}
            totalFans={creator.total_fans}
            heatScore={creator.heat_score}
            contentStreak={creator.content_streak}
            gates={stageGates}
          />
        </div>

        {/* ── Stats chips ── */}
        <div className="cp-stats-row">
          <div className="cp-stat-chip">
            <span className="cp-stat-chip-value">
              {(creator.total_fans || 0).toLocaleString()}
            </span>
            <span className="cp-stat-chip-label">Fans</span>
          </div>
          <div className="cp-stat-chip">
            <span className="cp-stat-chip-value">
              {creator.street_team_size || 0}
            </span>
            <span className="cp-stat-chip-label">Street Team</span>
          </div>
          <div className="cp-stat-chip">
            <span className="cp-stat-chip-value cp-stat-chip-value--fren">
              {FREN_STATUS_LABEL[creator.fren_status] || 'Coming Soon'}
            </span>
            <span className="cp-stat-chip-label">FReN</span>
          </div>
          {isHeatPublic(creator.heat_tier) && (
            <div className="cp-stat-chip">
            <span className="cp-stat-chip-value">
              {getHeatDisplay(creator.heat_tier)}
            </span>
            <span className="cp-stat-chip-label">Heat</span>
          </div>
          )}
        </div>

        {/* ── Street team SOUND earned (Sprint 5C) ── */}
        {streetTeamSoundEarned > 0 && (
          <p className="cp-team-sound">
            Street team has earned{' '}
            <span>{streetTeamSoundEarned.toLocaleString()} SOUND</span>
            {' '}supporting this artist
          </p>
        )}

        {/* ── Bio ── */}
        {creator.bio && (
          <p className="cp-bio">{creator.bio}</p>
        )}

        {/* ── Social links with platform colors ── */}
        {hasSocials && (
          <h3 className="cp-section-label">Listen &amp; follow</h3>
        )}
        {hasSocials && (
          <div className="cp-socials">
            {Object.entries(socials).map(([key, url]) => {
              if (!url) return null;
              const meta = SOCIAL_META[key];
              if (!meta) return null;
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cp-social-link"
                  style={{
                    '--platform-color': meta.color,
                    background: `${meta.color}22`,
                    borderColor: `${meta.color}55`,
                  }}
                  title={meta.label}
                >
                  <span className="cp-social-link-icon">
                    <BrandIcon platform={key} />
                  </span>
                  <span>{meta.label}</span>
                </a>
              );
            })}
          </div>
        )}

        {/* ── Latest updates (posts feed) ── */}
        <div className="cp-posts-section">
          <h2 className="cp-posts-title">Latest updates</h2>
          {postsLoading ? (
            <div className="cp-posts-loading">
              <div className="cp-spinner cp-spinner--sm" />
            </div>
          ) : posts.length === 0 ? (
            <p className="cp-posts-empty">No updates yet.</p>
          ) : (
            <div className="cp-posts-list">
              {posts.map(post => (
                <div key={post.id} className={`cp-post cp-post--${post.post_type}`}>
                  <div className="cp-post-header">
                    <span className="cp-post-type-icon">{POST_TYPE_ICON[post.post_type] || '📝'}</span>
                    {post.platform && (
                      <span className="cp-post-platform">{post.platform}</span>
                    )}
                    <span className="cp-post-time">{timeAgo(post.created_at)}</span>
                  </div>

                  {/* Media */}
                  {post.post_type === 'photo' && post.media_url && (
                    <img
                      src={post.media_url}
                      alt="Post"
                      className="cp-post-media-img"
                      loading="lazy"
                    />
                  )}
                  {post.post_type === 'video' && post.media_url && (
                    <video
                      src={post.media_url}
                      className="cp-post-media-video"
                      controls
                      playsInline
                      preload="metadata"
                    />
                  )}

                  {/* Content / link */}
                  {post.post_type === 'link' && post.content ? (
                    <a
                      href={post.content}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cp-post-link"
                    >
                      {post.content}
                    </a>
                  ) : post.content ? (
                    <p className="cp-post-content">{post.content}</p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Recruit CTA (Sprint 5C — dynamic copy) ── */}
        {!isOwnProfile && (() => {
          const rc = getRecruitCopy(creator.street_team_size || 0, creator.artist_name);
          return (
            <div style={{
              background: 'rgba(249,115,22,0.1)',
              border: '1px solid rgba(249,115,22,0.3)',
              borderRadius: '12px',
              padding: '16px',
              margin: '16px 0',
              textAlign: 'center',
            }}>
              <p className="cp-recruit-eyebrow">{rc.eyebrow}</p>
              <p style={{
                fontWeight: '600',
                fontSize: '15px',
                marginBottom: '4px',
              }}>
                {rc.headline}
              </p>
              <p style={{
                fontSize: '13px',
                opacity: 0.7,
                marginBottom: '12px',
              }}>
                Earn {sound(rates, 'recruit_creator')} once they confirm their email and make a first post — plus {sound(rates, 'fan_milestone_50')} more if they reach 50 fans
              </p>
              {/* existing recruit CTA logic */}
              <div className="cp-recruit-card">
                {!user_id ? (
                  <button className="cp-recruit-btn" onClick={handleRecruit}>
                    🎯 Recruit this creator — Sign in to earn <strong>{sound(rates, 'recruit_creator')}</strong>
                  </button>
                ) : isPlayer ? (
                  recruitDone ? (
                    <div className="cp-recruit-success">
                      {recruitResult?.already
                        ? <>🎯 You already recruited this creator.</>
                        : recruitResult?.pending === false && recruitResult.amount > 0
                          ? <>🎯 Recruited! <strong>+{recruitResult.amount.toLocaleString()} SOUND</strong> added to your balance.</>
                          : <>🎯 Recruited! <strong>{plus(rates, 'recruit_creator')} SOUND</strong> is released once they confirm their email and get active.</>}
                    </div>
                  ) : (
                    <>
                      <button
                        className="cp-recruit-btn"
                        onClick={handleRecruit}
                        disabled={recruitLoading}
                      >
                        {recruitLoading ? 'Recruiting…' : `🎯 Recruit this creator — ${plus(rates, 'recruit_creator')} SOUND`}
                      </button>
                      {recruitError && <p className="cp-recruit-error">{recruitError}</p>}
                    </>
                  )
                ) : null}
              </div>
              <p style={{ fontSize: '12px', opacity: 0.5, marginTop: '8px' }}>
                {rc.proof}
              </p>
            </div>
          );
        })()}

        {/* ── FReN Drop section ── */}
        <div className={`cp-fren-card ${frenLive ? 'cp-fren-card--live' : ''}`}>
          {frenLive ? (
            <>
              <div className="cp-fren-header">
                <span className="cp-fren-icon">🚀</span>
                <h2 className="cp-fren-title">FReN — Coming Soon</h2>
              </div>
              <p className="cp-fren-desc">
                FReN — Front Row collectibles for fans — coming soon.
              </p>
              {!isOwnProfile && (
                <span className="fren-coming-soon">🔜 Coming Soon</span>
              )}
              {waitlistCount > 0 && (
                <p className="cp-fren-count">{waitlistCount.toLocaleString()} fans waiting</p>
              )}
            </>
          ) : (
            <>
              <div className="cp-fren-header">
                <span className="cp-fren-icon">🎫</span>
                <h2 className="cp-fren-title">FReN Drop — Coming Soon</h2>
              </div>
              <p className="cp-fren-desc">
                FReN — Front Row collectibles for fans — coming soon.
                Join the waitlist to be first in line.
              </p>
              {!isOwnProfile && (
                <span className="fren-coming-soon">🔜 Coming Soon</span>
              )}
              {waitlistCount > 0 && (
                <p className="cp-fren-count">{waitlistCount.toLocaleString()} fans on the waitlist</p>
              )}
            </>
          )}
        </div>

        {/* ── Fan Playlists ── */}
        {!fanPlaylistsLoading && fanPlaylists.length > 0 && (
          <div className="cp-playlists-section">
            <h2 className="cp-playlists-title">🎵 Fan Playlists</h2>
            <div className="cp-playlists-list">
              {fanPlaylists.map(pl => {
                const plUrl    = pl.url || pl.playlist_url;
                const embedUrl = getEmbedUrl(plUrl, pl.platform);
                const embedH   = pl.platform === 'soundcloud' ? 120
                               : pl.platform === 'youtube'    ? 200
                               :                                152; // spotify default
                return (
                  <div key={pl.id} className="cp-playlist-item">
                    {embedUrl ? (
                      <iframe
                        src={embedUrl}
                        width="100%"
                        height={embedH}
                        frameBorder="no"
                        scrolling="no"
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                        title={`Fan playlist — ${pl.platform}`}
                        className="cp-playlist-embed"
                      />
                    ) : plUrl && plUrl.includes('music.apple.com') ? (
                      <a
                        href={plUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="music-link-card music-link-card--apple"
                      >
                        <div className="music-link-card-icon">🎵</div>
                        <div className="music-link-card-text">
                          <div className="music-link-card-label">Apple Music</div>
                          <div className="music-link-card-url">
                            {plUrl.replace('https://music.apple.com/', '').slice(0, 40)}…
                          </div>
                        </div>
                        <div className="music-link-card-arrow">→</div>
                      </a>
                    ) : (
                      <a
                        href={plUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cp-playlist-link"
                      >
                        <span className="cp-playlist-platform-icon">
                          {SOCIAL_META[pl.platform]
                            ? <BrandIcon platform={pl.platform} />
                            : '🔗'}
                        </span>
                        <span className="cp-playlist-url">{plUrl}</span>
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Edit profile CTA (own profile) ── */}
        {isOwnProfile && (
          <div className="cp-own-actions">
            <Link to="/creator-onboarding" className="cp-edit-btn">
              ✏️ Edit Profile
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
